import { useState, useEffect, useRef, useCallback } from 'react';
import { X, ChevronRight, ChevronDown, Folder, FolderOpen, FlaskConical } from 'lucide-react';
import api from '../../api';
import { QUALITY_SERVICE_API_URL } from '../../config';
import { useCurrentUser } from '../../context/CurrentUserContext';
import './CreateTestRunModal.css';

const STEP_LABELS = ['Test cycle', 'Test run', 'Select Tests'];

async function fetchAllCases(params) {
  const PAGE_SIZE = 100;
  let page = 1;
  let all = [];
  while (true) {
    const res = await api.get(`${QUALITY_SERVICE_API_URL}/hub/test-cases`, {
      params: { ...params, pageSize: PAGE_SIZE, page },
    });
    const batch = res.data?.data ?? [];
    all = all.concat(batch);
    if (batch.length < PAGE_SIZE) break;
    page++;
  }
  return all;
}

// Returns the chain of folder ids from the top-level down to (and including)
// the node with `targetId`, or null if not found.
function findFolderChain(nodes, targetId, acc = []) {
  for (const node of nodes ?? []) {
    const next = [...acc, node.id];
    if (node.id === targetId) return next;
    const found = findFolderChain(node.children, targetId, next);
    if (found) return found;
  }
  return null;
}

function StepIndicator({ step }) {
  return (
    <div className="ctr-steps">
      {STEP_LABELS.map((label, i) => {
        const n = i + 1;
        const state = n < step ? 'done' : n === step ? 'active' : 'pending';
        return (
          <div key={n} className={`ctr-step ctr-step-${state}`}>
            <span className="ctr-step-num">{n}</span>
            <span className="ctr-step-label">{label}</span>
            {i < STEP_LABELS.length - 1 && <span className="ctr-step-sep" />}
          </div>
        );
      })}
    </div>
  );
}

function FolderNode({ node, depth, expandedFolders, loadedFolders, selectedKeys, onToggleExpand, onToggleFolderCheck, onToggleCaseCheck }) {
  const isExpanded = expandedFolders.has(node.id);
  const loaded = loadedFolders[node.id];
  const cases = loaded?.cases ?? [];
  const loading = loaded?.loading ?? false;

  const allChildKeys = cases.map(c => c.key);
  const selectedCount = allChildKeys.filter(k => selectedKeys.has(k)).length;
  const folderChecked = allChildKeys.length > 0 && selectedCount === allChildKeys.length;
  const folderIndeterminate = selectedCount > 0 && selectedCount < allChildKeys.length;

  const cbRef = useRef(null);
  useEffect(() => {
    if (cbRef.current) {
      cbRef.current.indeterminate = folderIndeterminate;
    }
  }, [folderIndeterminate]);

  return (
    <div className="ctr-folder-node">
      <div className="ctr-folder-row" style={{ paddingLeft: `${8 + depth * 14}px` }}>
        <input
          ref={cbRef}
          type="checkbox"
          className="ctr-check"
          checked={folderChecked}
          onChange={() => onToggleFolderCheck(node)}
        />
        <button
          className="ctr-folder-expand"
          onClick={() => onToggleExpand(node.id)}
        >
          {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </button>
        {isExpanded ? <FolderOpen size={14} className="ctr-folder-icon" /> : <Folder size={14} className="ctr-folder-icon" />}
        <span className="ctr-folder-name">{node.name}</span>
        <span className="ctr-folder-count">{node.testCaseCount ?? ''}</span>
      </div>
      {isExpanded && (
        <div className="ctr-folder-children">
          {node.children?.map(child => (
            <FolderNode
              key={child.id}
              node={child}
              depth={depth + 1}
              expandedFolders={expandedFolders}
              loadedFolders={loadedFolders}
              selectedKeys={selectedKeys}
              onToggleExpand={onToggleExpand}
              onToggleFolderCheck={onToggleFolderCheck}
              onToggleCaseCheck={onToggleCaseCheck}
            />
          ))}
          {loading && <p className="ctr-loading-cases" style={{ paddingLeft: `${24 + depth * 14}px` }}>Loading…</p>}
          {!loading && cases.map(tc => (
            <div key={tc.key} className="ctr-case-row" style={{ paddingLeft: `${24 + depth * 14}px` }}>
              <input
                type="checkbox"
                className="ctr-check"
                checked={selectedKeys.has(tc.key)}
                onChange={() => onToggleCaseCheck(tc.key)}
              />
              <FlaskConical size={12} className="ctr-case-icon" />
              <span className="ctr-case-key">{tc.key}</span>
              <span className="ctr-case-summary">{tc.summary}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CreateTestRunModal({ isOpen, onClose, onCreated, onUpdated, selectedProject, mode = 'create', existingRun = null }) {
  const currentUser = useCurrentUser();
  const isEdit = mode === 'edit';
  const [step, setStep] = useState(1);
  const [cycles, setCycles] = useState([]);
  const [cyclesLoading, setCyclesLoading] = useState(false);
  const [cycleMode, setCycleMode] = useState('existing');
  const [selectedCycleId, setSelectedCycleId] = useState('');
  const [newCycleName, setNewCycleName] = useState('');
  const [newCycleVersion, setNewCycleVersion] = useState('');
  const [runName, setRunName] = useState('');
  const [folderTree, setFolderTree] = useState(null);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [loadedFolders, setLoadedFolders] = useState({});
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setCycleMode('existing');
    setNewCycleName('');
    setNewCycleVersion('');
    setFolderTree(null);
    setExpandedFolders(new Set());
    setLoadedFolders({});
    setSaving(false);
    setSaveError(null);

    if (isEdit && existingRun) {
      // Edit mode: jump straight to test selection, pre-fill from the existing run.
      setStep(3);
      setSelectedCycleId(existingRun.testCycleId ?? '');
      setRunName(existingRun.name ?? '');
      setSelectedKeys(new Set((existingRun.items ?? []).map(i => i.testCaseKey)));
    } else {
      setStep(1);
      setSelectedCycleId('');
      setRunName('');
      setSelectedKeys(new Set());
    }

    if (selectedProject) {
      setCyclesLoading(true);
      api.get(`${QUALITY_SERVICE_API_URL}/hub/test-execution/cycles`, { params: { project: selectedProject } })
        .then(res => setCycles((res.data ?? []).filter(c => c.endDate == null)))
        .catch(() => setCycles([]))
        .finally(() => setCyclesLoading(false));
    }
  }, [isOpen, selectedProject, isEdit, existingRun]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (step !== 3 || folderTree || foldersLoading || !selectedProject) return;
    setFoldersLoading(true);
    api.get(`${QUALITY_SERVICE_API_URL}/hub/test-cases/folders`, { params: { project: selectedProject } })
      .then(res => {
        setFolderTree(res.data);
        if (isEdit && existingRun) autoExpandForRun(res.data, existingRun);
      })
      .catch(() => setFolderTree(null))
      .finally(() => setFoldersLoading(false));
  }, [step, selectedProject, folderTree, foldersLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFolderCases = useCallback((folderId) => {
    if (loadedFolders[folderId]) return;
    setLoadedFolders(prev => ({ ...prev, [folderId]: { cases: [], loading: true } }));
    fetchAllCases({ folderId, getRecurssive: false, fields: 'key,summary' })
      .then(cases => {
        setLoadedFolders(prev => ({ ...prev, [folderId]: { cases, loading: false } }));
      }).catch(() => {
        setLoadedFolders(prev => ({ ...prev, [folderId]: { cases: [], loading: false } }));
      });
  }, [loadedFolders]);

  // Edit mode: expand the folders that contain already-selected tests (plus
  // their ancestors) and load their cases so the pre-checked tests are visible.
  function autoExpandForRun(tree, run) {
    const leafIds = [...new Set((run.items ?? []).map(i => i.folderId).filter(id => id != null))];
    if (leafIds.length === 0) return;
    const expandIds = new Set();
    leafIds.forEach(id => {
      const chain = findFolderChain(tree.children, id);
      if (chain) chain.forEach(cid => expandIds.add(cid));
    });
    setExpandedFolders(prev => new Set([...prev, ...expandIds]));
    leafIds.forEach(id => loadFolderCases(id));
  }

  function handleToggleExpand(folderId) {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
        loadFolderCases(folderId);
      }
      return next;
    });
  }

  function handleToggleFolderCheck(node) {
    const loaded = loadedFolders[node.id];
    const cases = loaded?.cases ?? [];
    const allKeys = cases.map(c => c.key);
    const allSelected = allKeys.length > 0 && allKeys.every(k => selectedKeys.has(k));

    if (allSelected) {
      setSelectedKeys(prev => {
        const next = new Set(prev);
        allKeys.forEach(k => next.delete(k));
        return next;
      });
    } else if (loaded) {
      setSelectedKeys(prev => {
        const next = new Set(prev);
        allKeys.forEach(k => next.add(k));
        return next;
      });
    } else {
      // Fetch all recursively then select
      setLoadedFolders(prev => ({ ...prev, [node.id]: { cases: [], loading: true } }));
      fetchAllCases({ folderId: node.id, getRecurssive: true, fields: 'key,summary' })
      .then(fetchedCases => {
        setLoadedFolders(prev => ({ ...prev, [node.id]: { cases: fetchedCases, loading: false } }));
        setSelectedKeys(prev => {
          const next = new Set(prev);
          fetchedCases.forEach(c => next.add(c.key));
          return next;
        });
      }).catch(() => {
        setLoadedFolders(prev => ({ ...prev, [node.id]: { cases: [], loading: false } }));
      });
    }
  }

  function handleToggleCaseCheck(key) {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  async function handleCreate() {
    setSaving(true);
    setSaveError(null);
    try {
      let cycleId = selectedCycleId;
      if (cycleMode === 'new') {
        const res = await api.post(`${QUALITY_SERVICE_API_URL}/hub/test-execution/cycle`, {
          name: newCycleName.trim(),
          project: selectedProject,
          ...(newCycleVersion.trim() && { version: { name: newCycleVersion.trim() } }),
        });
        cycleId = res.data.id;
      }

      const items = [...selectedKeys].map(key => ({
        testCaseKey: key,
        createdBy: currentUser?.email,
        updatedBy: currentUser?.email,
        status: 'unexecuted',
      }));

      await api.post(`${QUALITY_SERVICE_API_URL}/hub/test-execution/run`, {
        testCycleId: cycleId,
        name: runName.trim(),
        items,
      });

      onCreated(cycleId);
    } catch {
      setSaveError('Failed to create test run. Please try again.');
      setSaving(false);
    }
  }

  async function handleSave() {
    if (!existingRun) return;
    setSaving(true);
    setSaveError(null);
    try {
      await api.put(`${QUALITY_SERVICE_API_URL}/hub/test-execution/run/edit`, {
        testRunId: existingRun.testRunId,
        name: runName.trim(),
        testCaseKeys: [...selectedKeys],
        updatedBy: currentUser?.email,
      });
      onUpdated(existingRun.testRunId);
    } catch {
      setSaveError('Failed to save changes. Please try again.');
      setSaving(false);
    }
  }

  const step1Valid = cycleMode === 'existing' ? !!selectedCycleId : newCycleName.trim().length > 0;
  const step2Valid = runName.trim().length > 0;

  if (!isOpen) return null;

  return (
    <div className="ctr-overlay" onClick={onClose}>
      <div className="ctr-modal" onClick={e => e.stopPropagation()}>
        <div className="ctr-topbar">
          <StepIndicator step={step} />
          <button className="ctr-close-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="ctr-body">
          {/* ── Step 1: Cycle ── */}
          {step === 1 && (
            <div className="ctr-step-content">
              <h3 className="ctr-step-title">Select or create a test cycle</h3>
              <div className="ctr-cycle-mode-tabs">
                <button
                  className={`ctr-mode-tab ${cycleMode === 'existing' ? 'active' : ''}`}
                  onClick={() => setCycleMode('existing')}
                >
                  Use existing test cycle
                </button>
                <button
                  className={`ctr-mode-tab ${cycleMode === 'new' ? 'active' : ''}`}
                  onClick={() => setCycleMode('new')}
                >
                  Create new test cycle
                </button>
              </div>

              {cycleMode === 'existing' && (
                <div className="ctr-cycle-list">
                  {cyclesLoading && <p className="ctr-hint">Loading test cycles…</p>}
                  {!cyclesLoading && cycles.length === 0 && (
                    <p className="ctr-hint">No active test cycles found</p>
                  )}
                  {cycles.map(cycle => (
                    <label key={cycle.id} className={`ctr-cycle-item ${selectedCycleId === cycle.id ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="cycle"
                        value={cycle.id}
                        checked={selectedCycleId === cycle.id}
                        onChange={() => setSelectedCycleId(cycle.id)}
                      />
                      <span className="ctr-cycle-name">{cycle.name}</span>
                      {cycle.versionName && <span className="ctr-cycle-version">{cycle.versionName}</span>}
                    </label>
                  ))}
                </div>
              )}

              {cycleMode === 'new' && (
                <div className="ctr-new-cycle-form">
                  <label className="ctr-field-label">Test cycle name <span className="ctr-required">*</span></label>
                  <input
                    className="ctr-input"
                    type="text"
                    placeholder="e.g. iOS SDK v4.2.0"
                    value={newCycleName}
                    onChange={e => setNewCycleName(e.target.value)}
                  />
                  <label className="ctr-field-label">JIRA Release version</label>
                  <input
                    className="ctr-input"
                    type="text"
                    placeholder="e.g. v4.2.0"
                    value={newCycleVersion}
                    onChange={e => setNewCycleVersion(e.target.value)}
                  />
                </div>
              )}
            </div>
          )}

          {/* ── Step 2: Run name ── */}
          {step === 2 && (
            <div className="ctr-step-content">
              <h3 className="ctr-step-title">Name your test run</h3>
              <label className="ctr-field-label">Test run name <span className="ctr-required">*</span></label>
              <input
                className="ctr-input"
                type="text"
                placeholder="e.g. iOS SDK v4.2.0-rc01"
                value={runName}
                onChange={e => setRunName(e.target.value)}
                autoFocus
              />
            </div>
          )}

          {/* ── Step 3: Test case selection ── */}
          {step === 3 && (
            <div className="ctr-step-content ctr-tree-step">
              <h3 className="ctr-step-title">Select test cases</h3>
              <div className="ctr-tree-scroll">
                {foldersLoading && <p className="ctr-hint">Loading folder tree…</p>}
                {!foldersLoading && !folderTree && <p className="ctr-hint">No folders found.</p>}
                {!foldersLoading && folderTree && (
                  <div className="ctr-tree">
                    {(folderTree.children ?? []).map(node => (
                      <FolderNode
                        key={node.id}
                        node={node}
                        depth={0}
                        expandedFolders={expandedFolders}
                        loadedFolders={loadedFolders}
                        selectedKeys={selectedKeys}
                        onToggleExpand={handleToggleExpand}
                        onToggleFolderCheck={handleToggleFolderCheck}
                        onToggleCaseCheck={handleToggleCaseCheck}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="ctr-footer">
          {saveError && <span className="ctr-save-error">{saveError}</span>}
          {!isEdit && step > 1 && (
            <button className="ctr-btn-back" style={step === 3 ? { marginRight: 0 } : undefined} onClick={() => setStep(s => s - 1)} disabled={saving}>
              Back
            </button>
          )}
          {step === 3 && (
            <span className="ctr-selection-count">{selectedKeys.size} test{selectedKeys.size !== 1 ? 's' : ''} selected</span>
          )}
          <div className="ctr-footer-actions">
            {step < 3 && (
              <button
                className="ctr-btn-next"
                onClick={() => setStep(s => s + 1)}
                disabled={step === 1 ? !step1Valid : !step2Valid}
              >
                Next
              </button>
            )}
            {step === 3 && isEdit && (
              <button
                className="ctr-btn-create"
                onClick={handleSave}
                disabled={selectedKeys.size === 0 || saving || !runName.trim()}
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            )}
            {step === 3 && !isEdit && (
              <button
                className="ctr-btn-create"
                onClick={handleCreate}
                disabled={selectedKeys.size === 0 || saving}
              >
                {saving ? 'Creating…' : 'Create Test Run'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
